import { FC, HTMLAttributes, ReactNode } from "react";
import Image from "next/image";
import clsx from "clsx";
import { Crosshair, ShieldOff, Target } from "react-feather";

import CTAButton from "@/components/atoms/buttons/CTAButton";
import ActionButton from "@/components/atoms/buttons/ActionButton";
import { ITextButton } from "@/components/atoms/buttons/types";

// ----------------------------------------------------------------------------
// RedTeamingSection — sales-driven pitch for adversary-simulation engagements.
//
// Routed from the generic blocks.text-with-image-section when the block's
// heading/description mentions red team. Bespoke styling because the
// generic TextWithImage component's `background` enum is constrained by
// the Strapi schema (white | primary | secondary | primary_dark) and
// adding `red_gradient` to the CMS enum requires a migration.
//
// Visual: deep red gradient (red-900 → red-700 → red-600) + crimson glow
// orbs + diagonal grid pattern overlay. Reads as "we think like the
// attacker" — different from every blue/orange section on the page.
//
// Content shape (CMS-driven):
//   heading      — short punchy line ("Stop guessing what attackers can do")
//   description  — 1-2 sentence sales hook
//   buttons      — primary CTA + optional secondary
//   image        — optional, sits to the right
// ----------------------------------------------------------------------------

export type IRedTeamingSectionProps = HTMLAttributes<HTMLElement> & {
  heading?: string;
  description?: ReactNode;
  buttons?: ITextButton[];
  image?: { src: string; alt: string };
};

const VALUE_PILLS = [
  { Icon: Target, label: "Live exploit chains" },
  { Icon: Crosshair, label: "MITRE-mapped TTPs" },
  { Icon: ShieldOff, label: "Detection gap analysis" },
];

const RedTeamingSection: FC<IRedTeamingSectionProps> = ({
  heading,
  description,
  buttons,
  image,
  className,
  ...other
}) => {
  return (
    <section
      className={clsx(
        // Crimson gradient band — feels like a red-team operations brief.
        // bg layered with a subtle radial glow for depth.
        "relative isolate w-full overflow-hidden bg-gradient-to-br from-red-900 via-red-800 to-red-700 py-10 md:py-14 lg:py-16",
        className,
      )}
      {...other}
    >
      {/* Soft red + orange glow orbs add depth on top of the base
          gradient. Grid texture removed per design feedback — pure
          gradient reads cleaner. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-32 -top-32 h-80 w-80 rounded-full bg-red-500/40 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-32 -left-32 h-80 w-80 rounded-full bg-orange-500/30 blur-3xl"
      />

      <div className="container relative mx-auto">
        <div className="grid items-center gap-8 lg:grid-cols-12 lg:gap-12">
          {/* LEFT — copy + CTAs. Typography matches the canonical
              SectionHeader pattern used everywhere else (same eyebrow
              clamp, same title clamp, same description clamp + weight). */}
          <div className="text-center lg:col-span-7 lg:text-left">
            {/* Eyebrow — same scale + weight as SectionHeader, just
                colored for the red bg (no pulse dot). */}
            <p className="mb-3 font-bricolage-grotesque text-[clamp(0.75rem,0.4vw+0.65rem,0.875rem)] font-semibold uppercase tracking-[0.25em] text-red-200">
              Red team engagement
            </p>

            {heading && (
              <h2 className="font-bricolage-grotesque text-[clamp(1.375rem,3vw+0.75rem,3rem)] font-medium !leading-[1.1] text-white">
                {heading}
              </h2>
            )}

            {description && (
              <div className="mt-4 text-[clamp(0.875rem,0.5vw+0.75rem,1.125rem)] font-light leading-relaxed text-white/80 lg:font-extralight">
                {description}
              </div>
            )}

            {/* Value pills — three crisp deliverables. Compact horizontal
                row that reads as a spec sheet, not a feature list. */}
            <ul className="mt-5 flex flex-wrap justify-center gap-2 lg:justify-start">
              {VALUE_PILLS.map(({ Icon, label }) => (
                <li
                  key={label}
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/[0.06] px-3 py-1.5 text-xs font-normal text-white/90 backdrop-blur-sm md:text-sm"
                >
                  <Icon size={14} strokeWidth={2.2} className="text-red-200" />
                  {label}
                </li>
              ))}
            </ul>

            {/* CTAs — orange primary (real CTA, matches sitewide rule)
                + outlined secondary so it reads against the red bg. */}
            {buttons && buttons.length > 0 && (
              <div className="mt-7 flex flex-wrap items-center justify-center gap-3 lg:justify-start">
                {buttons[0] && (
                  <CTAButton
                    id={buttons[0].id}
                    href={buttons[0].link}
                    target={buttons[0].target}
                    variant="orange"
                    radius="full"
                    size="md"
                    showIcon
                    iconType="arrow"
                    iconPosition="right"
                  >
                    {buttons[0].text}
                  </CTAButton>
                )}
                {buttons[1] && (
                  <ActionButton
                    id={buttons[1].id}
                    href={buttons[1].link}
                    target={buttons[1].target}
                    variant="outline-brand"
                    size="md"
                    className="!border-white !text-white hover:!bg-white hover:!text-red-900"
                  >
                    {buttons[1].text}
                  </ActionButton>
                )}
              </div>
            )}
          </div>

          {/* RIGHT — image floats freely on the gradient. No white frame,
              no aspect-ratio crop, no shadow card — the image (typically
              a transparent PNG of the report mockup) sits naturally on
              the red gradient. Smaller max-width (was max-w-none) keeps
              it from competing with the copy column. */}
          {image?.src && (
            <div className="relative mx-auto w-full max-w-[220px] lg:col-span-5 lg:max-w-[280px] xl:max-w-[320px]">
              <div className="relative">
                <Image
                  src={image.src}
                  alt={image.alt}
                  width={320}
                  height={400}
                  sizes="(max-width: 1024px) 220px, 320px"
                  className="h-auto w-full object-contain"
                />
                {/* CONFIDENTIAL stamp in the corner — sells the artifact
                    as a sample of what the engagement produces. */}
                <span className="absolute right-2 top-2 rounded-md bg-red-700 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white shadow">
                  Sample · Redacted
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default RedTeamingSection;
